/**
 * Observe anchors and fire callback with ID as soon as a anchor is scrolled into view
 *
 * @param {Array<Object>} anchors - Array of React refs of anchor element
 * @param {Function} cb - Callback, fired if anchor is scrolled into view
 * @returns {Object} Public functions
 */
export default function AnchorManager (anchors = [], cb = () => void 0) {
    let ticking = false
    let activeId

    /**
     * Activate manager
     */
    function mount () {
        if (anchors.length) {
            activeId = null
            // avoid duplicate listeners
            window.removeEventListener('scroll', onScroll)
            window.addEventListener('scroll', onScroll)
            onScroll()
        }
    }

    /**
     * Deactivate manager and clean up
     */
    function unmount () {
        window.removeEventListener('scroll', onScroll)
    }

    /**
     * Triggered on scroll
     */
    function onScroll () {
        if (!ticking) {
            window.requestAnimationFrame(update)
        }
        ticking = true
    }

    /**
     * Add new anchor to observed array
     * @param {Object} anchor - React ref of anchor element
     */
    function registerAnchor (anchor) {
        anchors.push(anchor)
        update()
    }

    /**
     * Triggered on scroll for each animation frame.
     * Find anchor in view or closest one above the fold.
     */
    function update () {
        // screen center
        const center = window.innerHeight / 3
        // remove unmounted elements
        const elements = anchors.map(a => a.current).filter(a => !!a)
        if (!(elements && elements.length)) {
            ticking = false
            return
        }
        // find closest anchor
        let active =
            elements.find(anchor => {
                const { top } = anchor.getBoundingClientRect()
                return top <= center && top >= 0
            })
        if (!active) {
            elements.unshift(null)
            active = elements.reduce((prevAnchor, currAnchor) => {
                const { top: b } = currAnchor.getBoundingClientRect()
                if (!prevAnchor) {
                    if(b < center) {
                        return currAnchor
                    }
                    return null
                }
                const { top: a } = prevAnchor.getBoundingClientRect()

                if (b < 0 && -b - center <= -a - center) {
                    return currAnchor
                } else if (a < 0 && -b - center <= -a - center) {
                    return prevAnchor
                }
                return null
            })
        }
        active = active || { id: 'top' }
        if (active && active.id !== activeId) {
            cb(active.id)
            activeId = active.id
        }

        ticking = false
    }

    update()

    return {
        mount,
        unmount,
        registerAnchor
    }
}
